import React, { useRef, useState } from 'react';
import { API, axios } from '../../../utils/api';
import { classi, messaggioErrore } from '../../accesso/campi';
import { preferenzeVisita, motiviNonInteressa } from '../../../utils/immobili';
import Icona from '../Icone';
import { useTraduzioni, contesto, PRINCIPALE } from '../../../i18n';
import { useScossone } from '../SezioniHome';

// Risposta del cliente su un immobile: "Sì, fissate la visita" (fasce orarie + nota facoltativa) oppure
// "Non ci interessa" (motivo obbligatorio; con "Altro" anche due parole). La usano la card dell'elenco
// (compatta), la card grande in cima alla scheda e i bottoni "Prenotate un'altra visita" / "Non ci interessa più".
// Mentre si aspetta il server: Invio e clic ripetuti non mandano una seconda risposta, "Annulla" è spento.
// Se il server rifiuta (400: la casa è cambiata, es. da un'altra finestra) il messaggio resta qui e
// onRifiuto(messaggio) permette alla pagina di rileggere i dati.

// La conferma della risposta: nel pannello, o nell'avviso della pagina se la casa esce dall'elenco (ImmobiliCliente)
export const testoInviata = (scelta, consulente, lingua = PRINCIPALE) => {
  const { t } = contesto(lingua);
  if (scelta === 'forse') {
    return t('immobili.decisione.grazie_forse',
             { nome: consulente?.nome || t('comune.il_consulente') });
  }
  if (scelta !== 'si') return t('immobili.decisione.inviata_no');
  return consulente?.nome ? t('immobili.decisione.inviata_si', { nome: consulente.nome }) : t('immobili.decisione.inviata_si_noi');
};

// onFatto(scelta): la risposta è arrivata al server
export const RispostaImmobile = ({ immobile, scelta, consulente, onFatto, onAnnulla, onRifiuto, compatta = false }) => {
  const { t, l, errore: tradErrore } = useTraduzioni();
  const [fasce, setFasce] = useState([]);
  const [motivo, setMotivo] = useState(null);
  const [nota, setNota] = useState('');
  const [attesa, setAttesa] = useState(false);
  const inVolo = useRef(false); // subito, prima che React ridisegni: il secondo Invio non passa
  const [errore, setErrore] = useState(null);
  const [inviata, setInviata] = useState(false);
  const { scossa, scuoti, fine } = useScossone();
  const nome = consulente?.nome || t('comune.il_consulente');

  const sbaglio = (testo) => { setErrore(testo); scuoti(); };
  const alterna = (chiave) => setFasce((f) => (f.includes(chiave) ? f.filter((x) => x !== chiave) : [...f, chiave]));

  const invia = async (e) => {
    e.preventDefault();
    if (inVolo.current) return;
    if (scelta === 'no' && !motivo) { sbaglio(t('immobili.decisione.scegliete_motivo')); return; }
    if (scelta === 'no' && motivo === 'altro' && !nota.trim()) { sbaglio(t('immobili.decisione.scrivete_motivo')); return; }
    inVolo.current = true;
    setAttesa(true);
    setErrore(null);
    const id = encodeURIComponent(immobile.id);
    try {
      if (scelta === 'si') {
        await axios.post(`${API}/immobili/${id}/richiesta-visita`, { preferenze: fasce, nota: nota.trim() });
      } else if (scelta === 'forse') {
        // la via di mezzo: dice sì senza chiedere la visita (backend/server.py, mi_interessa)
        await axios.post(`${API}/immobili/${id}/mi-interessa`);
      } else {
        await axios.post(`${API}/immobili/${id}/non-interessa`, { motivo, nota: nota.trim() });
      }
      // la risposta è arrivata: il pannello non si può più rimandare (se la pagina non riesce a rileggere i
      // dati, qui resta la conferma invece di un bottone che gira per sempre)
      setInviata(true);
      onFatto(scelta);
    } catch (err) {
      const messaggio = tradErrore(messaggioErrore(err, t('immobili.decisione.errore')));
      sbaglio(messaggio);
      inVolo.current = false;
      setAttesa(false);
      const stato = err.response?.status;
      if (onRifiuto && stato >= 400 && stato < 500 && stato !== 401) onRifiuto(messaggio);
    }
  };

  if (inviata) {
    return (
      <div className={classi(compatta && 'pannello')} data-testid={`risposta-${scelta}`}>
        <div className="err ok" role="status">{testoInviata(scelta, consulente, l)}</div>
      </div>
    );
  }

  return (
    <form className={classi(compatta && 'pannello', scossa && 'shake')} onAnimationEnd={fine} onSubmit={invia} data-testid={`risposta-${scelta}`}>
      {scelta === 'si' ? (
        <div className="opts" role="group" aria-label={t('immobili.decisione.gruppo_si')}>
          <span className="lab">{t('immobili.decisione.quando_comodo')}</span>
          {preferenzeVisita(l).map(([chiave, etichetta]) => (
            <button type="button" key={chiave} className={fasce.includes(chiave) ? 'on' : undefined} aria-pressed={fasce.includes(chiave)}
              onClick={() => alterna(chiave)}>{etichetta}</button>
          ))}
        </div>
      ) : (
        <div className="opts" role="group" aria-label={t('immobili.decisione.gruppo_no')}>
          <span className="lab">{t('immobili.decisione.perche')}</span>
          {motiviNonInteressa(l).map(([chiave, etichetta]) => (
            <button type="button" key={chiave} className={motivo === chiave ? 'on' : undefined} aria-pressed={motivo === chiave}
              onClick={() => { setMotivo(chiave); setErrore(null); }}>{etichetta}</button>
          ))}
        </div>
      )}
      <div className="send">
        <div className="note">
          {/* segnaposto breve: a 390 px "Una nota per Ernesto (facoltativa)" era tagliato (il nome resta nell'etichetta del campo) */}
          <input value={nota} onChange={(e) => { setNota(e.target.value); setErrore(null); }} maxLength={500}
            aria-label={scelta === 'si' ? t('immobili.decisione.nota_si', { nome }) : t('immobili.decisione.nota_no')}
            placeholder={scelta === 'si' ? t('immobili.decisione.nota_si_segnaposto')
              : t(motivo === 'altro' ? 'immobili.decisione.nota_no' : 'immobili.decisione.nota_no_segnaposto')} />
        </div>
        <button type="submit" className={classi('btn p sm', attesa && 'busy')} aria-busy={attesa} disabled={attesa}>{t('comune.invia')}</button>
      </div>
      {errore && <div className="err" role="alert">{errore}</div>}
      {onAnnulla && <button type="button" className="annulla" onClick={onAnnulla} disabled={attesa}>{t('comune.annulla')}</button>}
    </form>
  );
};

// Riga "Volete vederla?" nella card dell'elenco
export const DecisioneCard = ({ immobile, consulente, onFatto, onRifiuto }) => {
  const { t } = useTraduzioni();
  const [scelta, setScelta] = useState(null);
  return (
    <div className="dec" data-testid="decisione-card">
      <div className="q">{t('immobili.decisione.volete_vederla')}</div>
      {scelta ? (
        <RispostaImmobile immobile={immobile} scelta={scelta} consulente={consulente} onFatto={onFatto} onRifiuto={onRifiuto}
          onAnnulla={() => setScelta(null)} compatta />
      ) : (
        <div className="bt">
          <button type="button" className="btn sm p" onClick={() => setScelta('si')}>{t('immobili.decisione.si')}</button>
          <button type="button" className="btn sm" onClick={() => setScelta('forse')}>{t('immobili.decisione.forse')}</button>
          <button type="button" className="btn sm no" onClick={() => setScelta('no')}>{t('immobili.decisione.no')}</button>
        </div>
      )}
    </div>
  );
};

// Card grande in cima alla scheda
export const DecisioneScheda = ({ immobile, consulente, provenienza, onFatto, onRifiuto }) => {
  const { t } = useTraduzioni();
  const [scelta, setScelta] = useState(null);
  return (
    <section className="decide r" data-testid="decisione-scheda">
      <div className="k">{provenienza}</div>
      <h2>{t('immobili.decisione.titolo_scheda')}</h2>
      <p>{consulente?.nome ? t('immobili.decisione.testo_scheda', { nome: consulente.nome }) : t('immobili.decisione.testo_scheda_noi')}</p>
      <div className="g2">
        <button type="button" className={classi('big si', scelta === 'si' && 'on')} aria-pressed={scelta === 'si'} onClick={() => setScelta('si')}>
          <Icona nome="si" /><div><b>{t('immobili.decisione.si')}</b><span>{t('immobili.decisione.si_sotto')}</span></div>
        </button>
        <button type="button" className={classi('big', scelta === 'forse' && 'on')} aria-pressed={scelta === 'forse'} onClick={() => setScelta('forse')}>
          <Icona nome="cuore" /><div><b>{t('immobili.decisione.forse')}</b><span>{t('immobili.decisione.forse_sotto')}</span></div>
        </button>
        <button type="button" className={classi('big no', scelta === 'no' && 'on')} aria-pressed={scelta === 'no'} onClick={() => setScelta('no')}>
          <Icona nome="no" /><div><b>{t('immobili.decisione.no')}</b><span>{t('immobili.decisione.no_sotto')}</span></div>
        </button>
      </div>
      {scelta && <RispostaImmobile key={scelta} immobile={immobile} scelta={scelta} consulente={consulente} onFatto={onFatto} onRifiuto={onRifiuto} />}
    </section>
  );
};
